import { createCrudService } from '../api/baseService';
import easyCheckApi, { easyCheckRequest } from '../api/config';
import { handleApiError } from '../../utils/errorHandler';
import { useState, useCallback, useMemo } from 'react';

const beneficiaryBaseCrud = createCrudService('beneficiarymaster');

export const beneficiaryService = {
  ...beneficiaryBaseCrud,

  // Beneficiary-specific endpoints
  getBeneficiariesByCompany: async () => {
    return easyCheckRequest(() => 
      easyCheckApi.get('/beneficiarymaster/company')
    );
  },

  validateBeneficiaryName: async (beneficiaryName) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/beneficiarymaster/validate-name', { beneficiaryName })
    );
  }
};

export const easyCheckBeneficiaryService = {
  getAllBeneficiaries: async (pageNumber = 1, pageSize = 10) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/beneficiarymaster?pageNumber=${pageNumber}&pageSize=${pageSize}`)
    );
  },

  getBeneficiaryById: async (id) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/beneficiarymaster/${id}`)
    );
  },

  createBeneficiary: async (beneficiaryData) => {
    console.log('👤 Creating beneficiary:', beneficiaryData);
    const result = await easyCheckRequest(() => 
      easyCheckApi.post('/beneficiarymaster', beneficiaryData)
    );
    console.log('👤 Create beneficiary result:', result);
    return result;
  },

  updateBeneficiary: async (id, beneficiaryData) => {
    return easyCheckRequest(() => 
      easyCheckApi.put(`/beneficiarymaster/${id}`, beneficiaryData)
    );
  },

  deleteBeneficiary: async (id) => {
    return easyCheckRequest(() => 
      easyCheckApi.delete(`/beneficiarymaster/${id}`)
    );
  },

  // Additional utility methods
  getBeneficiariesByStatus: async (isActive) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/beneficiarymaster?isActive=${isActive}`)
    );
  },

  // Import functionality
  importBeneficiaries: async (formData) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/beneficiarymaster/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
  },

  downloadTemplate: async () => {
    return easyCheckRequest(() => 
      easyCheckApi.get('/beneficiarymaster/template', {
        responseType: 'blob',
      })
    );
  }
};

// Custom hook for using beneficiary service with local state management
export const useBeneficiaryService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const executeRequest = useCallback(async (requestFn) => {
    setLoading(true);
    setError(null);
    try {
      const result = await requestFn();
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      return { success: false, message: err.message || 'An error occurred' };
    } finally {
      setLoading(false);
    }
  }, []);

  const getAllBeneficiaries = useCallback((pageNumber, pageSize) => executeRequest(() => easyCheckBeneficiaryService.getAllBeneficiaries(pageNumber, pageSize)), [executeRequest]);
  const getBeneficiaryById = useCallback((id) => executeRequest(() => easyCheckBeneficiaryService.getBeneficiaryById(id)), [executeRequest]);
  const createBeneficiary = useCallback((beneficiaryData) => executeRequest(() => easyCheckBeneficiaryService.createBeneficiary(beneficiaryData)), [executeRequest]);
  const updateBeneficiary = useCallback((id, beneficiaryData) => executeRequest(() => easyCheckBeneficiaryService.updateBeneficiary(id, beneficiaryData)), [executeRequest]);
  const deleteBeneficiary = useCallback((id) => executeRequest(() => easyCheckBeneficiaryService.deleteBeneficiary(id)), [executeRequest]);
  const getBeneficiariesByStatus = useCallback((isActive) => executeRequest(() => easyCheckBeneficiaryService.getBeneficiariesByStatus(isActive)), [executeRequest]);
  const importBeneficiaries = useCallback((formData) => executeRequest(() => easyCheckBeneficiaryService.importBeneficiaries(formData)), [executeRequest]);
  const downloadTemplate = useCallback(() => executeRequest(() => easyCheckBeneficiaryService.downloadTemplate()), [executeRequest]);

  return useMemo(() => ({
    loading,
    error,
    getAllBeneficiaries,
    getBeneficiaryById,
    createBeneficiary,
    updateBeneficiary,
    deleteBeneficiary,
    getBeneficiariesByStatus,
    importBeneficiaries,
    downloadTemplate
  }), [loading, error, getAllBeneficiaries, getBeneficiaryById, createBeneficiary, updateBeneficiary, deleteBeneficiary, getBeneficiariesByStatus, importBeneficiaries, downloadTemplate]);
}; 